/* eslint-disable guard-for-in */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import React, { useContext, useState, useMemo, useRef, useEffect } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PageTitle } from 'components/layouts/pageTitle';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Config } from 'configs';
import { SelectedItemButton } from 'components/buttons/selectedItemButton';
import { Input } from 'components/formInputs/input';
import { files } from 'codemash';
import { FormButton } from 'components/buttons/formButton';
import { apiActionChooser } from 'utils/apiActionChooser';
import { postFile } from 'api/client/requests';
import { MenuCategories } from './_categories';

const menuTypes = [
  { value: 'Images', label: 'Menu images' },
  { value: 'Link', label: 'External link' },
  { value: 'Products', label: 'Products' },
];

export const MenuAddEditForm = ({ itemData, isEdit }) => {
  const navigate = useNavigate();

  const { state: companyState, addNew, replaceById, getCompanyRestaurants } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [currentType, setCurrentType] = useState('Images');
  const [selectedRestaurants, setSelectedRestaurants] = useState(itemData?.restaurants || []);

  const categoriesRef = useRef(null);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    type: yup.string().required('Please enter a type.').default(itemData?.type),
    restaurants: yup
      .array()
      .of(yup.string())
      .required('Please enter restaurants.')
      .min(1, 'Please select at least one restaurant.'),
    external_link: yup.string().default(itemData?.external_link),
  });

  const {
    register,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setSelectedRestaurants(itemData?.restaurants || []);
    setCurrentType(itemData?.type || 'Images');

    reset(itemData);
  }, [itemData, reset]);

  const getRestaurantsDataSource = () => {
    const allRestaurants = [{ _id: '-1', name: '-' }, ...(companyState.restaurants || [])];

    return allRestaurants
      ?.filter((r) => !selectedRestaurants.includes(r._id))
      .map((restaurant) => ({
        label: restaurant.name,
        value: restaurant._id,
      }));
  };

  const deleteSelectedRestaurant = (restaurantId) => {
    const newRestaurants = selectedRestaurants.filter((r) => r !== restaurantId);

    setSelectedRestaurants(newRestaurants);
    setValue('restaurants', newRestaurants);
  };

  const onSubmit = async (data) => {
    setLoading(true);

    const submitData = {
      restaurants: data.restaurants,
      type: data.type,
      categories: [],
      category_images: [],
    };

    // index: images
    const imagesToUpload = {};

    if (data.type === 'Images') {
      const categories = categoriesRef.current.getData();

      if (categories) {
        let currentImagesInd = 0;

        categories.forEach((category) => {
          submitData.categories.push({ category_id: category.category_id, name: category.name, order: category.order });

          if (category.images) {
            category.images.forEach((imageData) => {
              submitData.category_images.push({ category_id: category.category_id, languages: imageData.languages || [], images: [] });

              imagesToUpload[currentImagesInd] = imageData.images;
              currentImagesInd += 1;
            });
          }
        });
      }
    } else if (data.type === 'Link') {
      submitData.external_link = data.external_link;
    }

    for (const imgInd in imagesToUpload) {
      const imagesAtInd = imagesToUpload[imgInd];

      if (imagesAtInd) {
        for (let i = 0; i < imagesAtInd.length; i += 1) {
          if (imagesAtInd[i].id) {
            submitData.category_images[imgInd].images.push(imagesAtInd[i].id);
          } else {
            await apiActionChooser(auth.company?.company, async () => {
              const fileResp = await files.uploadRecordFile({
                collectionName: Config.DATA.COLLECTIONS.MENU,
                database: Config.CODEMASH.DATABASE_ID,
                uniqueFieldName: `category_images.${imgInd}.images`,
                secretKey: auth.token,
                file: imagesAtInd[i].file,
              });

              submitData.category_images[imgInd].images.push(fileResp.key);
            }, async () => {
              const fileResp = await postFile({
                fieldPath: `category_images.${imgInd}.images`,
                file: imagesAtInd[i].file,
                resource: 'Menu',
              });

              submitData.category_images[imgInd].images.push(fileResp.id);
            });
          }
        }
      }
    }

    if (!isEdit) {
      await addNew(
        {
          collectionName: Config.DATA.COLLECTIONS.MENU,
          data: submitData,
          token: auth.token,
          resourceV2: 'AdminMenu',
          companyIdForV2: auth.company?.company,
        },
        async () => {
          navigate('/menu');
          setLoading(false);
        },
        () => {
          setLoading(false);
        },
      );
    } else {
      await replaceById(
        {
          collectionName: Config.DATA.COLLECTIONS.MENU,
          id: itemData._id,
          data: submitData,
          token: auth.token,
          resourceV2: 'AdminMenu',
          companyIdForV2: auth.company?.company,
        },
        async () => {
          navigate('/menu');
          setLoading(false);
        },
        () => {
          setLoading(false);
        },
      );
    }
  };

  const onError = (data) => console.log(data);

  const renderSelectedRestaurants = () => {
    if (!selectedRestaurants) return null;

    return (
      <div>
        {selectedRestaurants.map((restaurant) => {
          const restaurantData = companyState.restaurants?.find((r) => r._id === restaurant);

          if (!restaurantData) return null;
          return (
            <div key={restaurant} className="mt-2">
              <SelectedItemButton
                key={restaurantData._id}
                name={restaurantData?.name}
                onClick={() => deleteSelectedRestaurant(restaurantData?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <PageTitle
        title="Menu"
        breadcrumbs={[{ path: '/menu', title: 'Menus' }]}
      />
      <div className="container mx-auto px-2 md:px-6 lg:px-8">
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {isEdit ? 'Edit menu' : 'Add new menu'}
                  </h3>
                </div>

                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 lg:col-span-3">
                    <DropDownList
                      showLabel
                      id="restaurants"
                      name="restaurants"
                      label="Restaurants*"
                      options={getRestaurantsDataSource()}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      // disabled={isEdit}
                      onChange={(e) => {
                        setSelectedRestaurants((oldArray) => [...oldArray, e.target.value]);
                        setValue('restaurants', [...selectedRestaurants, e.target.value]);
                      }}
                    />
                    {errors?.restaurants && (
                      <p className="mt-2 text-sm text-red-600" id="email-error">
                        {errors?.restaurants.message}
                      </p>
                    )}
                    {renderSelectedRestaurants()}
                  </div>

                  <div className="col-span-6 lg:col-span-3">
                    <DropDownList
                      {...register('type')}
                      showLabel
                      id="type"
                      label="Menu display"
                      options={menuTypes}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      onChange={(e) => setCurrentType(e.target.value)}
                    />
                    {errors?.type && (
                    <p className="mt-2 text-sm text-red-600" id="email-error">
                      {errors?.type.message}
                    </p>
                    )}
                  </div>

                  {currentType === 'Link' && (
                    <div className="col-span-6 lg:col-span-3">
                      <label htmlFor="external_link" className="block text-sm font-medium text-gray-700">
                        Link to menu
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <Input
                          {...register('external_link')}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          id="external_link"
                          type="text"
                          errorMessage={errors?.external_link}
                        />
                      </div>
                      {errors?.external_link && (
                      <p className="mt-2 text-sm text-red-600" id="external_link-error">
                        {errors?.external_link.message}
                      </p>
                      )}
                    </div>
                  )}

                  {currentType === 'Images' && (
                  <MenuCategories
                    itemData={itemData}
                    ref={categoriesRef}
                  />
                  )}
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="button"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => navigate('/menu')}
                  disabled={loading}
                >
                  Cancel
                </button>
                <FormButton text="Save" isLoading={loading} />
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
